import { requireCompletedProfile } from "@hisani/auth";
import { applyToPostingSchema, decideApplicationSchema, getResumeSnapshotSchema, listApplicationsForPostingSchema, stableConflict, stableInvalidTransition, stableNotFound, withdrawApplicationSchema, } from "../validation.js";
import { requireFeatureMembership } from "./sealed-record-support.js";
import { jobApplicationResponse, resumeSnapshotResponse, } from "./hire-response-projections.js";
const FEATURE = "HIRE";
function applicationMutationResult(result, notFoundMessage) {
    switch (result.outcome) {
        case "UPDATED":
        case "REPLAYED":
            return jobApplicationResponse(result.application);
        case "NOT_FOUND":
            if (notFoundMessage === undefined)
                throw stableNotFound();
            throw stableConflict(notFoundMessage);
        case "STALE_VERSION":
            throw stableConflict("This application has changed. Refresh and retry.");
        case "IDEMPOTENCY_CONFLICT":
            throw stableConflict("This mutation identifier was already used for a different Hire command.");
    }
}
/**
 * Submits an application. Login is the only gate — never a subscription
 * check (`docs/products/stable.md` "Hire": free regardless of the posting
 * Stable's tier), and there is no Stable-membership check at all: applying
 * is open to "any Horseman on the platform ... regardless of Stable
 * membership". Reads the applicant's current live Resume and freezes it
 * into an immutable snapshot as part of the same atomic submission
 * (`StableRepository.submitApplication`).
 */
export async function applyToPosting(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = applyToPostingSchema.parse(input);
    const resume = await dependencies.repository.getResume(actor.account.id);
    const now = dependencies.clock.now();
    const result = await dependencies.repository.submitApplication({
        id: dependencies.newId(),
        postingId: parsed.postingId,
        applicantHorsemanId: actor.account.id,
        applicantDisplayName: actor.account.displayName,
        conversationId: dependencies.newId(),
        snapshotId: dependencies.newId(),
        resumeBio: resume.bio,
        resumeSkills: resume.skills,
        resumeWorkHistory: resume.workHistory,
        clientMutationId: parsed.clientMutationId,
        commandFingerprint: dependencies.identifierFingerprint.fingerprint(JSON.stringify(["HIRE_APPLY", parsed.postingId])),
        now,
    });
    if (result.outcome === "POSTING_NOT_FOUND")
        throw stableNotFound();
    if (result.outcome === "POSTING_NOT_OPEN") {
        throw stableInvalidTransition("This posting is no longer accepting applications.");
    }
    if (result.outcome === "ALREADY_ACTIVE") {
        throw stableConflict("You already have an active application for this posting.");
    }
    if (result.outcome === "IDEMPOTENCY_CONFLICT") {
        throw stableConflict("This mutation identifier was already used for a different Hire command.");
    }
    return jobApplicationResponse(result.application);
}
export async function withdrawApplication(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = withdrawApplicationSchema.parse(input);
    const result = await dependencies.repository.withdrawApplication({
        applicationId: parsed.applicationId,
        applicantHorsemanId: actor.account.id,
        clientMutationId: parsed.clientMutationId,
        commandFingerprint: dependencies.identifierFingerprint.fingerprint(JSON.stringify([
            "HIRE_WITHDRAW",
            parsed.applicationId,
            parsed.expectedVersion,
        ])),
        expectedVersion: parsed.expectedVersion,
        now: dependencies.clock.now(),
    });
    return applicationMutationResult(result);
}
export async function listMyApplications(principal, dependencies) {
    const actor = requireCompletedProfile(principal);
    const applications = await dependencies.repository.listApplicationsForApplicant(actor.account.id);
    return applications.map(jobApplicationResponse);
}
export async function listApplicationsForPosting(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listApplicationsForPostingSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "VIEW");
    const applications = await dependencies.repository.listApplicationsForPosting(parsed.stableId, parsed.postingId);
    return applications.map(jobApplicationResponse);
}
export async function decideApplication(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = decideApplicationSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    const result = await dependencies.repository.decideApplication({
        stableId: parsed.stableId,
        applicationId: parsed.applicationId,
        actorId: actor.account.id,
        decision: parsed.decision,
        clientMutationId: parsed.clientMutationId,
        commandFingerprint: dependencies.identifierFingerprint.fingerprint(JSON.stringify([
            "HIRE_DECIDE",
            parsed.stableId,
            parsed.applicationId,
            parsed.decision,
            parsed.expectedVersion,
        ])),
        expectedVersion: parsed.expectedVersion,
        now: dependencies.clock.now(),
    });
    return applicationMutationResult(result, "This application is no longer active, or does not belong to this Stable.");
}
/** The disclosed Resume snapshot for one application — visible only to the
 * posting Stable's management tier, never re-derived from the applicant's
 * current (possibly since-changed) live Resume. */
export async function getResumeSnapshot(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = getResumeSnapshotSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "VIEW");
    const snapshot = await dependencies.repository.getResumeSnapshot({
        stableId: parsed.stableId,
        applicationId: parsed.applicationId,
    });
    if (snapshot === null)
        throw stableNotFound();
    return resumeSnapshotResponse(snapshot);
}
